/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数は React component ではなく
 * テーブルセルの render 関数。 親コンポーネント再 render 時に再生成されるが、
 * MasterDataTable は cell の戻り値を <TableCell> の children として表示するだけで
 * 独立した component subtree を持たないため、 lint 警告の前提 (= subtree 破棄)
 * に該当しない。 renderRowActions も同様。
 */
import type { MouseEvent } from 'react'

import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type SingularDayCategory,
  SingularDayDemandBehavior,
} from '@ebalance/proto/bffuser/v1/singular_day_model_pb'
import { useTranslation } from 'react-i18next'

import { TertiaryButton, Text } from '@enechain/ecloud-designsystem'

import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { convertAreaLabel } from '~/models/area'
import { useSingularGroupNameMap } from '~/pages/AdminSingularDay/hooks/useSingularGroupNames'
import {
  resolveSingularGroupName,
  singularDayCategoryLabelKey,
  singularDayDemandBehaviorLabelKey,
} from '~/pages/AdminSingularDay/models/options'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

/**
 * 一覧テーブルが必要とする最小の行型。 `useListSingularDays` の
 * `SingularDayRow` はこれを満たす superset (updatedAt 等の楽観ロック用
 * field を追加で持つ)。
 */
export type SingularDayTableRow = {
  /** surrogate UUID identity。 getRowKey に使う。 */
  id: string
  /** `YYYY-MM-DD`。 一覧の日付セルに使う。 */
  dateKey: string
  date: Date
  name: string
  category: SingularDayCategory
  demandBehavior: SingularDayDemandBehavior
  /** 特異グループ番号 (1〜10)。 demandBehavior=SINGULAR の時のみ意味を持つ。 */
  singularGroup: number
  /** 空 = 全エリア共通、 1〜10 は特定エリア。 */
  areaCodes: AreaCode[]
}

type Props<T extends SingularDayTableRow> = {
  rows: T[]
  /** 行クリック / 編集ボタンで編集モーダルを開く。 未指定なら行は非クリック。 */
  onRowClick?: (row: T, event?: MouseEvent) => void
  isLoading?: boolean
}

/**
 * 特異日一覧テーブル (design demand/special-day-calendar.html 準拠)。
 * 列: 日付 / 曜日 (土=青・日=赤) / 名称 / エリア / カテゴリ / 需要扱い / 編集。
 * エリアは空 = 全エリア共通、 1〜10 は選択エリア名を「・」区切りで表示する。
 * 需要扱い列は SINGULAR 行で既定名「特異グループN」(AC-MSD-002)、 それ以外は
 * 従来の需要扱いラベル。 日付昇順の並べ替えは呼び出し元 (`index.tsx`) が担う。
 * 空状態は MasterDataTable の emptyLabel で「該当する特異日がありません」を
 * 表示する。
 */
export const SingularDayTable = <T extends SingularDayTableRow>({
  rows,
  onRowClick,
  isLoading,
}: Props<T>): React.ReactElement => {
  const { t } = useTranslation()
  const { groupNames: namesByGroup } = useSingularGroupNameMap()

  const columns: Array<MasterDataTableColumn<T>> = [
    {
      key: 'date',
      header: t('page:adminSingularDay.table.date'),
      // design fmtDate は YYYY/MM/DD だが、 F2 test (`SingularDayTable.test.tsx`)
      // が dateKey (YYYY-MM-DD) を getByText で固定しているためここは dateKey のまま。
      // 変更するには test も同時に直す (design 突合時に見送った nit として残置)
      cell: (row) => row.dateKey,
    },
    {
      key: 'weekday',
      header: t('page:adminSingularDay.table.weekday'),
      align: 'center',
      cell: (row) => {
        const dow = row.date.getDay()
        // design 規約: 土=青 (Text.infoHigh) / 日=赤 (Text.negativeHigh)。
        // design/css インライン `.sdc-dow--sat { color: var(--text-info-high) }`
        // / `.sdc-dow--sun { color: var(--text-negative-high) }` および
        // ui-feasibility/screens/admin-singular-day.md §4b (実測 2026-08-04) が SoT。
        const color =
          dow === 0
            ? 'Text.negativeHigh'
            : dow === 6
              ? 'Text.infoHigh'
              : undefined
        return (
          <Text as="span" color={color}>
            {t(`page:adminSingularDay.weekday.${WEEKDAY_KEYS[dow]}`)}
          </Text>
        )
      },
    },
    {
      key: 'name',
      header: t('page:adminSingularDay.table.name'),
      cell: (row) => row.name,
    },
    {
      key: 'area',
      header: t('page:adminSingularDay.table.area'),
      cell: (row) =>
        row.areaCodes.length === 0 ? (
          <Text as="span" color="Text.midEmphasis">
            {t('page:adminSingularDay.table.areaAll')}
          </Text>
        ) : (
          row.areaCodes.map((c) => convertAreaLabel(c)).join('・')
        ),
    },
    {
      key: 'category',
      header: t('page:adminSingularDay.table.category'),
      align: 'center',
      cell: (row) => t(singularDayCategoryLabelKey(row.category)),
    },
    {
      key: 'demandBehavior',
      header: t('page:adminSingularDay.table.demandBehavior'),
      align: 'center',
      // 特異グループ日は表示名 (命名済みなら保存名、 未命名なら既定名
      // 「特異グループN」、 AC-MSD-002 / AC-MSD-011 ※注記)。 それ以外は従来の
      // 需要扱いラベル。
      cell: (row) =>
        row.demandBehavior === SingularDayDemandBehavior.SINGULAR
          ? resolveSingularGroupName(row.singularGroup, namesByGroup, t)
          : t(singularDayDemandBehaviorLabelKey(row.demandBehavior)),
    },
  ]

  return (
    <MasterDataTable
      columns={columns}
      emptyLabel={t('page:adminSingularDay.table.empty')}
      getRowKey={(row) => row.id}
      isLoading={isLoading}
      onRowClick={onRowClick}
      renderRowActions={
        onRowClick !== undefined
          ? (row) => (
              <TertiaryButton
                onClick={(e?: MouseEvent) => {
                  onRowClick(row, e)
                }}
                size="md"
              >
                {t('page:adminSingularDay.table.edit')}
              </TertiaryButton>
            )
          : undefined
      }
      rows={rows}
    />
  )
}
